import React, { useCallback, useContext, useEffect, useState } from 'react';
import { IssuesCloseOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { ErrorStatus } from '../../../models';
import { UserContext } from '../../contexts';
import { ErrorStatusEditPage } from './error-status-edit-page';
import { EntityChangeLog } from '../../reports';

export function ErrorStatusesPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [errorStatuses, setErrorStatuses] = useState<ErrorStatus[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalErrorStatus, seteditModalErrorStatus] = useState<ErrorStatus>();
  const [canEdit, setCanEdit] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEdit(hasPermission(user, 'errorStatuses.edit'));
  }, [user]);

  const loadErrorStatuses = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`error-statuses`)
      .then((response: any) => {
        setErrorStatuses(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadErrorStatuses();
  }, [loadErrorStatuses]);

  const onRefreshClick = () => {
    loadErrorStatuses();
  };

  const onErrorStatusEditSaved = () => {
    closeEditModal();
    loadErrorStatuses();
  };

  const openEditModal = () => {
    seteditModalErrorStatus(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByErrorStatus = (errorStatus: ErrorStatus) => {
    seteditModalErrorStatus(errorStatus);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    seteditModalErrorStatus(undefined);
  };

  const deleteErrorStatus = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`error-statuses/${id}`)
      .then(() => {
        setLoading(false);
        loadErrorStatuses();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteErrorStatus = (id: string) => {
    const onOk = () => deleteErrorStatus(id);
    Modal.confirm({
      title: 'Hata statüsü sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Hata statüsü silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
    if (e.key === 'edit') {
      const errorStatus = errorStatuses?.find(m => m.id === id);
      if (errorStatus) {
        openEditModalByErrorStatus(errorStatus);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmDeleteErrorStatus(id);
    }
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, errorStatus: ErrorStatus) => (
        <Button type="link" onClick={() => openEditModalByErrorStatus(errorStatus)}>
          {name}
        </Button>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, errorStatus: ErrorStatus) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(errorStatus.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <IssuesCloseOutlined /> Hata statüleri
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" onClick={openEditModal} icon={<PlusOutlined />} type="primary">
                Yeni hata statüsü
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={errorStatuses} loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'ErrorStatus'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal title="Hata statüsü" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <ErrorStatusEditPage errorStatus={editModalErrorStatus} onCancel={closeEditModal} onSaved={onErrorStatusEditSaved} />
      </Modal>
    </>
  );
}
